import { Dispatch } from 'redux';
import axios, { AxiosResponse } from 'axios';
import i18n from 'i18next';

import { DOMAIN_EU, DOMAIN_SG, DOMAIN_IN, DEFAULT_URL, REDIRECT_URL } from '../../base';
import cfg from '../../config.json';
import { ApiResponseType, UserType } from '../../interface';
import { apiBaseUrl, networkCall } from '../../utils/networkCall';

import {
	addKey,
	addSID,
	checkKey,
	IAddKeyActionType,
	IcheckKeyActionType,
	IAddSIDActionType,
} from '../actions/apiKey.action';
import {
	addCompanyName,
	addFullProfile,
	addName,
	addPicture,
	IAddCompanyNameActionType,
	IAddNameActionType,
	IAddPictureActionType,
	AddFullProfileActionType,
} from '../actions/profile.action';

import { TokenResponseType } from './types';
import { callBalanceAPI } from '../actions/balance.action';
import { getGlobalC2COption } from '../actions/C2CModal.action';
import { subaccountCustomerTypeId } from '../../constants';
import { toggleChatbotViewState } from '../actions/chatbotView.action';
import { toggleChatbotCustomCodeViewState } from '../actions/chatbotCustomCodeView.action';
import Cookies from 'js-cookie';

export const LOGIN_REQUESTED = 'login/LOGIN_REQUESTED';
export const LOGIN_SUCCEEDED = 'login/LOGIN_SUCCEEDED';
export const LOGIN_FAILED = 'login/LOGIN_FAILED';
export const LOGOUT_REQUESTED = 'login/LOGOUT_REQUESTED';
export const LOGOUT_SUCCEEDED = 'login/LOGOUT_SUCCEEDED';
export const LOGOUT_FAILED = 'login/LOGOUT_FAILED';

const TOKEN_COUNT = 'KLR_FE_TOKEN_COUNT';
const FE_TOKEN = 'KLR_FE_TOKEN';
const FE_SID = 'KLR_FE_SID';
const KLR_USER_POD = 'KLR_USER_POD';

export const ACCESS_TOKEN = 'ACCESS_TOKEN';
export const REFRESH_TOKEN = 'REFRESH_TOKEN';

type DispatchType =
	| RequestLoginType
	| LoginFailedType
	| LoginSucceededType
	| IcheckKeyActionType
	| IAddCompanyNameActionType
	| IAddNameActionType
	| IAddPictureActionType
	| AddFullProfileActionType
	| IAddKeyActionType
	| IAddSIDActionType
	// added for balance API call, need to fix to concrete type.
	| any;

export const redirectToLogin = (isDefaultNext: boolean = false) => {
	let redirectUrl =
		'https://' +
		DOMAIN_SG +
		cfg.LOGIN_URL +
		'?next=' +
		Buffer.from(window.location.pathname + window.location.search).toString(
			'base64'
		);

	if (isDefaultNext) {
		redirectUrl =
			'https://' +
			DOMAIN_SG +
			cfg.LOGIN_URL +
			'?next=' +
			Buffer.from(DEFAULT_URL).toString('base64');
	}
	// Removed Next Parameter in the logout flow -CNP-3195
	window.location.href = window.location.origin + cfg.LOG_0UT;
};

const dispatchLoginFailed = (dispatch: Dispatch<LoginFailedType>) => (
	error: string
) => {
	dispatch(loginFailed());
};

const getProfile = (dispatch: Dispatch<DispatchType>) => {

	const keyCount = localStorage.getItem(TOKEN_COUNT);
	const apiKey = localStorage.getItem(FE_TOKEN);
	const sID = localStorage.getItem(FE_SID);
	const dispatchFailed = dispatchLoginFailed(dispatch);
	if (keyCount !== null) {
		if (keyCount !== '0') {
			if (apiKey && sID) {
				networkCall('get', `${cfg.PROFILE}`, apiBaseUrl.baseUrl)
					.then((response: any) => {
						const { code, data } = response as ApiResponseType<
							UserType
						>;
						if (data) {
							if (code === 'RBC201') {
								redirectToLogin();
							} else {
								if (data?.language !== 'en') {
									i18n.changeLanguage(data?.language);
								}
								if (!subaccountCustomerTypeId.includes(data?.customer_type_id)) {
									document.cookie = "is_parent_as_child_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
									localStorage.removeItem("for-user-id");
								}
								if (data?.privileges?.prices?.main) {
									dispatch(callBalanceAPI());
								}
								dispatch(addName(data?.full_name));
								dispatch(addPicture(data?.profile_picture));
								dispatch(addCompanyName(data?.company_name));
								dispatch(addFullProfile(data));
								dispatch(addKey(apiKey));
								dispatch(addSID(sID));
								dispatch(checkKey(true));
								dispatch(loginSucceeded());
								dispatch(getGlobalC2COption());
								dispatch(toggleChatbotViewState(data?.features.includes('CHATBOT_VIEW')));
								dispatch(toggleChatbotCustomCodeViewState(String(data?.chatbot_custom_code) === '1'));
							}
						} else {
							console.log('Profile is empty');
						}
					})
					.catch(() => {
						dispatchFailed('');
						redirectToLogin();
					});
			} else {
				dispatchFailed('Local storage token API or SID does not exist');
			}
		} else {
			dispatchFailed('');
			dispatch(checkKey(false));
		}
	} else {
		dispatchFailed('Local storage token count does not exist');
	}
};

const getToken = (dispatch: Dispatch<DispatchType>) => {
	axios
		.get(`${window.location.origin}/api/v1/getToken`)
		.then((response: AxiosResponse<ApiResponseType<TokenResponseType>>) => {
			const { data } = response;
			if (data?.data) {
				const { data: apiData, code } = data;
				if (code === 'RBC2001') {
					redirectToLogin();
				} else {
					if (code === 'RBC103') {
						const { hostname } = window?.location;
						const {
							user_pod: userPod,
							api_key: apiKey,
							api_key_count: apiKeyCount,
							api_key_sid: sid,
							accessToken:accessToken,
							refreshToken:refreshToken
						} = apiData;
						if (userPod) {
							if ((userPod === 'SG' && hostname !== DOMAIN_SG) || (userPod === 'EU' && hostname !== DOMAIN_EU) || (userPod === 'IN' && hostname !== DOMAIN_IN)) {
								redirectToLogin();
								return;
							}
						}
						if(accessToken && refreshToken){
							localStorage.setItem(ACCESS_TOKEN, accessToken);
							localStorage.setItem(REFRESH_TOKEN, refreshToken);

							Cookies.set('refresh_token',refreshToken);
						}

						if (apiKey && apiKeyCount && sid) {
							localStorage.setItem(TOKEN_COUNT, apiKeyCount);
							localStorage.setItem(FE_SID, sid);
							localStorage.setItem(FE_TOKEN, apiKey);
							localStorage.setItem(KLR_USER_POD, userPod);

							getProfile(dispatch);
						}
					}
				}
			}
		})
		.catch(() => {
			redirectToLogin();
		});
};

export type RequestLoginType = {
	type: typeof LOGIN_REQUESTED;
};

export const requestLogin = () => {
	return (dispatch: Dispatch<DispatchType>) => {
		dispatch({
			type: LOGIN_REQUESTED,
		});
		if (window.location.hostname === 'localhost') {
			getProfile(dispatch);
		} else {
			getToken(dispatch);
		}
	};
};

export type RequestLogoutType = {
	type: typeof LOGOUT_REQUESTED;
};

export const requestLogout = () => {

	return (
		dispatch: Dispatch<RequestLogoutType | LogoutSucceeded | LogoutFailed>
	) => {
		dispatch({
			type: LOGOUT_REQUESTED,
		});

		localStorage.clear();
		sessionStorage.clear();
		window.location.href = REDIRECT_URL + cfg.LOG_0UT;

	};
};

export type LoginSucceededType = {
	type: typeof LOGIN_SUCCEEDED;
};

export const loginSucceeded = (): LoginSucceededType => ({
	type: LOGIN_SUCCEEDED,
});

export type LoginFailedType = {
	type: typeof LOGIN_FAILED;
};

export const loginFailed = (): LoginFailedType => ({
	type: LOGIN_FAILED,
});

export type LogoutSucceeded = {
	type: typeof LOGOUT_SUCCEEDED;
};

export const logoutSucceeded = (): LogoutSucceeded => ({
	type: LOGOUT_SUCCEEDED,
});

export type LogoutFailed = {
	type: typeof LOGOUT_FAILED;
};

export const logoutFailed = (): LogoutFailed => ({
	type: LOGOUT_FAILED,
});

export type Action = RequestLoginType | LoginSucceededType | LoginFailedType;